import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuthContext } from './hooks/useAuthContext';
import { useLang } from './hooks/useLang';

import Home from './pages/Home';
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import Onboarding from './pages/auth/Onboarding';
import User from './pages/User';
import Contact from './pages/Contact';
import About from './pages/About';
import Events from './pages/Events';
import Careers from './pages/Careers';
import Library from './pages/Library';
import CategoriesPage from './components/CategoriesPage';
import PDFViewer from './pages/PDFViewer';
import NotFound from './pages/NotFound';
import Forms from './pages/Forms';
import CreateForm from './pages/CreateForm';
import FormView from './pages/FormView';
import Profile from './pages/Profile';
import AdminPanel from './pages/AdminPanel';
import Settings from './pages/Settings';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import AuthGateCard from './components/common/AuthGateCard';

const VALID_LANGS = ['en', 'az', 'ru'];
const DEFAULT_LANG = 'en';

const LangGuard = ({ children }) => {
	const { lang } = useParams();
	if (!VALID_LANGS.includes(lang)) return <NotFound />;
	return children;
};

// The root single-segment route ("/:lang") doubles as a public profile route
// ("/<username>") — whichever the segment isn't a known language code.
const RootRoute = () => {
	const { lang: slug } = useParams();
	const { user } = useAuthContext();
	const hasCompletedOnboarding = (user && user.status) ? true : false;

	if (VALID_LANGS.includes(slug)) {
		if (!user) return <LangGuard><Home /></LangGuard>;
		return hasCompletedOnboarding ? <User /> : <Navigate to={`/${slug}/onboarding`} replace />;
	}

	// The admin panel hides behind a 48-character hex path. Usernames cap at
	// 30 characters, so the shapes can never overlap and a normal profile
	// visit never pays for this check. The slug itself is not in this bundle —
	// AdminPanel asks the server whether the segment is the real one.
	if (/^[a-f0-9]{48}$/.test(slug || '')) return <AdminPanel slug={slug} />;

	// A logged-out visitor can still view a public profile; only a logged-in
	// user who hasn't finished onboarding gets redirected there instead.
	if (user && !hasCompletedOnboarding) return <Navigate to={`/${DEFAULT_LANG}/onboarding`} replace />;
	return <Profile username={slug} />;
};

const ProtectedRoute = ({ children }) => {
	const { user } = useAuthContext();
	const { language } = useLang();
	const hasCompletedOnboarding = (user && user.status) ? true : false;

	if (!user) {
		return <AuthGateCard />;
	}

	if (!hasCompletedOnboarding) {
		return <Navigate to={`/${language}/onboarding`} replace />;
	}

	return children;
};

const AppRoutes = () => {
	const { user, authIsReady } = useAuthContext();
	const location = useLocation();
	const { language } = useLang();

	const hasCompletedOnboarding = (user && user.status) ? true : false;
    
    // Show nothing (or a global loader) until we know if the user is authenticated
    if (!authIsReady) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-background text-foreground">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
        );
    }

	return (
		<Routes>
			<Route exact path='/' element={<Navigate to={`/${language}`} replace />} />

			<Route exact path='/:lang' element={<RootRoute />} />

			<Route path='/:lang/signup' element={!user ? <Signup /> : (hasCompletedOnboarding ? <Navigate to={`/${language}`} replace /> : <Navigate to={`/${language}/onboarding`} replace />)} />

			<Route path='/:lang/login' element={!user ? <Login /> : (hasCompletedOnboarding ? <Navigate to={`/${language}`} replace /> : <Navigate to={`/${language}/onboarding`} replace />)} />

			<Route path='/:lang/forgot-password' element={!user ? <ForgotPassword /> : (hasCompletedOnboarding ? <Navigate to={`/${language}`} replace /> : <Navigate to={`/${language}/onboarding`} replace />)} />

			<Route path='/:lang/reset-password' element={!user ? <ResetPassword /> : (hasCompletedOnboarding ? <Navigate to={`/${language}`} replace /> : <Navigate to={`/${language}/onboarding`} replace />)} />

			<Route
				path='/:lang/onboarding'
				element={user ? (!hasCompletedOnboarding ? <Onboarding /> : <Navigate to={`/${language}/dashboard`} replace />) : <Navigate to={`/${language}/login`} replace />}
			/>

			<Route path='/:lang/dashboard' element={<ProtectedRoute><User /></ProtectedRoute>} />
			<Route path='/:lang/profile' element={<ProtectedRoute><User /></ProtectedRoute>} />
            <Route path='/:lang/analytics' element={<ProtectedRoute><User /></ProtectedRoute>} />
            <Route path='/:lang/settings' element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path='/:lang/notes' element={<ProtectedRoute><User /></ProtectedRoute>} />
            <Route path='/:lang/library' element={<ProtectedRoute><User /></ProtectedRoute>} />
            <Route path='/:lang/categories' element={<ProtectedRoute><User /></ProtectedRoute>} />
            <Route path='/:lang/add-category' element={<ProtectedRoute><User /></ProtectedRoute>} />
            {/* <Route path='/:lang/community' element={<ProtectedRoute><User /></ProtectedRoute>} /> */}
            {/* <Route path='/:lang/ai' element={<ProtectedRoute><User /></ProtectedRoute>} /> */}
            {/* <Route path='/:lang/opportunities' element={<ProtectedRoute><User /></ProtectedRoute>} /> */}
            <Route path='/:lang/forms' element={<ProtectedRoute><Forms /></ProtectedRoute>} />
            <Route path='/:lang/forms/create' element={<ProtectedRoute><CreateForm /></ProtectedRoute>} />

			<Route path='/:lang/contact' element={<Contact />} />
			<Route path='/:lang/about' element={<About />} />
			<Route path='/:lang/events' element={<Events />} />
			<Route path='/:lang/careers' element={<Careers />} />
			<Route path='/:lang/privacy' element={<Privacy />} />
			<Route path='/:lang/terms' element={<Terms />} />
			<Route path='/:lang/material/:id' element={<ProtectedRoute><PDFViewer /></ProtectedRoute>} />

			<Route path='/v/:slug' element={<FormView />} />

			<Route path='/:lang/*' element={<NotFound />} />

			<Route path='*' element={<NotFound />} />
		</Routes>
	);
};

function App() {
	return (
		<BrowserRouter>
			<AppRoutes />
		</BrowserRouter>
	);
};

export default App;